import React, { useContext, useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { SignJWT } from "jose";
import { LoginGoogle, LoginWK, getMicrosoftUser } from "./Auth.api";
import "./Login.css";
import { AuthContext } from "./AuthProvider";
// import Registration from "./Registration";
// import ForgotPassword from "./ForgotPassword";
import Loader from "../components/ui/Loader";

const Login = () => {
  //#region VARS --------------------------------------------------------
  const { manager, user, setUser, setIsAuthenticated, fullClient, monday } =
    useContext(AuthContext);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [domain, setDomain] = useState(
    fullClient?.emailDomain?.[0] ?? "@workobot.com"
  );
  var { instance } = useMsal();

  //Auth Methods override
  const AUTH_METHODS_OVERRIDE = manager.authMethodsOverride || [];
  const authMicrosoft =
    AUTH_METHODS_OVERRIDE.length > 0
      ? AUTH_METHODS_OVERRIDE.includes("microsoft")
      : fullClient?.authMethods?.microsoft;
  const authGoogle =
    AUTH_METHODS_OVERRIDE.length > 0
      ? AUTH_METHODS_OVERRIDE.includes("google")
      : fullClient?.authMethods?.google;
  const authWorkobot =
    AUTH_METHODS_OVERRIDE.length > 0
      ? AUTH_METHODS_OVERRIDE.includes("workobot")
      : fullClient?.authMethods.workobot;
  const validDomains =
    process.env.REACT_APP_LOGIN_WORKOBOT_DOMAINS_OVERRIDE?.split(",") ||
    fullClient?.emailDomain;

  // const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  // const [showForgotPassword, setShowForgotPassword] = useState(false);

  const isMountedRef = useRef(true); // para evitar problemas al desmontar el componente | el padre se re-renderiza y no deja terminar los proceso
  //#endregion VARS -----------------------------------------------------

  useEffect(() => {
    const initializeGoogleSignIn = () => {
      if (!user) {
        const googleId = window.google?.accounts?.id;

        if (googleId) {
          googleId.initialize({
            client_id:
              process.env.REACT_APP_GOOGLE_CREDENTIAL_ID ||
              fullClient?.authMethods?.google?.clientId,
            callback: handleGoogleSignIn,
          });

          googleId.renderButton(document.getElementById("googleSignIn"), {
            theme: "outline",
            size: "large",
            locale: "es",
            text: "signin_with",
            shape: "rectangular",
            width: 300,
          });

          googleId.prompt();
        }
      }
    };

    if (authGoogle) {
      if (window.google) {
        initializeGoogleSignIn();
      } else if (!document.querySelector("#google-script")) {
        const googleScript = document.createElement("script");
        googleScript.src = "https://accounts.google.com/gsi/client";
        googleScript.async = true;
        googleScript.defer = true;
        googleScript.id = "google-script";
        googleScript.onload = initializeGoogleSignIn;
        document.body.appendChild(googleScript);
      }
    }
    // MICROSOFT SE INICIALIZA EN AUTHPROVIDER ----------------------------------------------
  }, [fullClient]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleLoginWorkobot = async (event) => {
    event.preventDefault();

    if (!email.trim() || !password.trim()) {
      setErrorMessage("Por favor, completa todos los campos.");
      return;
    }

    const fullEmail = email + domain;

    setIsLoading(true);
    try {
      const result = await LoginWK(fullEmail, password, fullClient.hubCode);
      if (isMountedRef.current) {
        if (result.Message === "SUCCESSFUL LOGIN") {
          const tokenPayload = {
            user: result.user,
          };

          // console.log({ monday });

          const secret = new TextEncoder().encode(monday);
          const token = await new SignJWT(tokenPayload)
            .setProtectedHeader({ alg: "HS256" })
            .setExpirationTime("7d")
            .sign(secret);

          localStorage.setItem("w_wkt", token);

          setUser(result.user);
          setIsAuthenticated(true);
        } else {
          setIsLoading(false);
          setErrorMessage("Usuario inválido, verifica las credenciales.");
          console.log(result.Message);
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        setErrorMessage(
          "Ocurrió un problema al intentar iniciar sesión. Por favor, inténtalo nuevamente."
        );

        setIsLoading(false);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleUserInputChange = (e) => {
    const updatedEmail = e.target.value.replace(/@/g, "");
    setEmail(updatedEmail);
  };

  async function handleGoogleSignIn(e) {
    setIsLoading(true);

    try {
      const result = await LoginGoogle(e.credential, fullClient.hubCode);
      setIsLoading(false);
      if (isMountedRef.current) {
        if (result.Message === "VALID USER") {
          localStorage.setItem(
            "g_wkt",
            JSON.stringify([e.credential, fullClient.hubCode])
          );
          setUser(result.user);
          setIsAuthenticated(true);
        } else {
          setErrorMessage("Usuario inválido, verifica las credenciales.");

          console.log(result.Message);
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        setErrorMessage(
          "Ocurrió un problema al intentar iniciar sesión. Por favor, inténtalo nuevamente."
        );

        setIsLoading(false);
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }

  const handleMicrosoftLogin = async () => {
    try {
      const loginResponse = await instance.loginPopup({
        scopes: ["user.read"],
        prompt: "select_account",
      });

      if (loginResponse && loginResponse.account) {
        const tokenResponse = await instance.acquireTokenSilent({
          scopes: ["user.read"],
          account: loginResponse.account,
        });

        const accessToken = tokenResponse.accessToken;

        const result = await getMicrosoftUser(accessToken, fullClient.hubCode);

        if (isMountedRef.current) {
          if (result.id) {
            localStorage.setItem(
              "ms_wkt",
              JSON.stringify([accessToken, fullClient.hubCode])
            );

            let resUser = {
              user: result.userPrincipalName,
              userNames: result.displayName,
              id: result.id,
            };
            setUser(resUser);
            setIsAuthenticated(true);
          } else {
            setErrorMessage("Usuario inválido, verifica las credenciales.");
          }
        }
      }
    } catch (error) {
      console.error("Error en el login de Microsoft: ", error);
      if (isMountedRef.current) {
        setErrorMessage(
          "Ocurrió un problema al intentar iniciar sesión. Por favor, inténtalo nuevamente."
        );
      }
    }
  };

  if (isLoading || !fullClient) {
    return <Loader myText="cargando" />;
  }

  return (
    <div className="login">
      <div className="login__container">
        {/* {showRegistrationModal ? (
          <Registration
            fullClient={fullClient}
            onClose={() => setShowRegistrationModal(false)}
            monday={monday}
          />
        ) : showForgotPassword && email.length > 0 ? (
          <ForgotPassword
            onBack={() => setShowForgotPassword(false)}
            email={`${email}${domain}`}
            hubcode={fullClient.hubCode}
          />
        ) : ( */}
        <>
          <h2 className="login__title">Inicio de sesión</h2>

          {/*Login Google | Microsoft*/}
          <div className="social_btns p-2">
            {authGoogle && <div id="googleSignIn" />}
            {authMicrosoft && (
              <button
                className="login__button--microsoft"
                onClick={handleMicrosoftLogin}
              >
                <img
                  src="https://cdn-icons-png.flaticon.com/512/732/732221.png"
                  alt="Microsoft"
                />
                Iniciar sesión con Microsoft
              </button>
            )}
          </div>

          {/*Login Workobot*/}
          {authWorkobot && (
            <>
              <br />
              {(authGoogle || authMicrosoft) && <hr />}

              {/* <p className="login__description">
                  {
                    "Si ya estás registrado en Workobot, ingresa tu email y contraseña."
                  }
                </p> */}

              <form className="login__form">
                <div className="login__input-group">
                  <input
                    type="text"
                    className="login__input login__input--email"
                    value={email}
                    onChange={handleUserInputChange}
                    placeholder="Email..."
                    autoComplete="username"
                    required
                  />
                  <select
                    className="login__input login__input--domain"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                  >
                    {validDomains ? (
                      validDomains?.map((item, index) => (
                        <option key={index} value={item}>
                          {item}
                        </option>
                      ))
                    ) : (
                      <option value="@workobot.com">@workobot.com</option>
                    )}
                  </select>
                </div>
                <div className="login__input-group">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="login__input login__input--password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={"Contraseña..."}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className="login__toggle-password"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? "Ocultar" : "Mostrar"}
                  </button>
                </div>
                {errorMessage && (
                  <div className="login__error-message">{errorMessage}</div>
                )}
                {
                  <div className="login__aux-options">
                    {/* <a
                        href="ignore"
                        className={`login__link ${
                          email.length === 0 ? "disabled" : ""
                        }`}
                        onClick={() =>
                          email.length > 0
                            ? setShowForgotPassword(true)
                            : setErrorMessage(
                                "Debes ingresar un correo electrónico para intentar recuperar la contraseña."
                              )
                        }
                      >
                        ¿Olvidaste tu contraseña?
                      </a> */}
                  </div>
                }
              </form>
              <div className="login__footer">
                <button
                  type="submit"
                  // className="login__button"
                  className="btn-primary"
                  onClick={handleLoginWorkobot}
                >
                  Iniciar sesión
                </button>

                {/* <a
                    href="ignore"
                    className="login__footer-register"
                    onClick={() => setShowRegistrationModal(true)}
                  >
                    Registrar usuario
                  </a> */}
              </div>
            </>
          )}
        </>
        {/* )} */}
      </div>
    </div>
  );
};

export default React.memo(Login);
